import React, { useState, useMemo } from "react";
import { Shell, Header, Tab } from "./Shell";
import { ReqRow, MonthCalendar, PolicyTab } from "./Shared";
import ChangePassword from "./ChangePassword";
import TeamAdmin from "./TeamAdmin";
import PtoReport from "./PtoReport";
import EmployeeView from "./EmployeeView";
import LogTimeOff from "./LogTimeOff";
import { s } from "../lib/theme";
import { fmt } from "../lib/pto";
import { Requests, errMsg } from "../lib/api";

function UpcomingRow({ r, refreshAll }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  async function doDelete() {
    setBusy(true);
    try {
      await Requests.remove(r.id);
      await refreshAll();
    } catch (e) {
      alert(errMsg(e, "Could not delete this entry."));
      setBusy(false);
      setConfirming(false);
    }
  }

  return (
    <div style={{ ...s.coverRow, flexWrap: "wrap" }} data-testid={`upcoming-row-${r.id}`}>
      <div style={s.coverDate}>{fmt(r.start)}{r.end !== r.start ? ` – ${fmt(r.end)}` : ""}</div>
      <div style={{ flex: 1, minWidth: 200 }}>
        <strong>{r.user_name}</strong>
        <span style={{ color: "#6b7280", fontSize: 13 }}> · {r.type_label}</span>
      </div>
      <div style={{ fontSize: 13, color: "#6b7280" }}>
        {r.business_days} day{r.business_days === 1 ? "" : "s"}
      </div>
      {!confirming ? (
        <button
          data-testid={`upcoming-delete-${r.id}`}
          style={{ ...s.btnGhost, padding: "4px 10px", fontSize: 12, color: "#b91c1c", borderColor: "#fca5a5" }}
          onClick={() => setConfirming(true)}
        >
          🗑 Delete
        </button>
      ) : (
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "#7f1d1d" }}>
            Delete{Number(r.hours) > 0 ? ` & refund ${r.hours} hrs` : ""}?
          </span>
          <button data-testid={`upcoming-delete-yes-${r.id}`} disabled={busy} style={{ ...s.btnDeny, padding: "4px 10px", fontSize: 12 }} onClick={doDelete}>
            {busy ? "…" : "Yes"}
          </button>
          <button data-testid={`upcoming-delete-no-${r.id}`} disabled={busy} style={{ ...s.btnGhost, padding: "4px 10px", fontSize: 12 }} onClick={() => setConfirming(false)}>No</button>
        </div>
      )}
    </div>
  );
}

export default function AdminView({ me, users, requests, refreshAll, onLogout }) {
  const [tab, setTab] = useState("queue");
  const [previewEmployee, setPreviewEmployee] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [err, setErr] = useState("");
  const [histEmp, setHistEmp] = useState("all");
  const [histStatus, setHistStatus] = useState("all");
  const [histFrom, setHistFrom] = useState("");
  const [histTo, setHistTo] = useState("");
  const [calEmp, setCalEmp] = useState("all");
  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });

  const pending = useMemo(
    () => requests.filter((r) => r.status === "pending").sort((a, b) => (a.created_at || "").localeCompare(b.created_at || "")),
    [requests]
  );
  const decided = useMemo(
    () => requests.filter((r) => r.status !== "pending").sort((a, b) => (b.decided_at || "").localeCompare(a.decided_at || "")),
    [requests]
  );

  const teamMembers = useMemo(() => users.filter((u) => u.role !== "admin"), [users]);
  const histFiltered = useMemo(
    () => decided.filter((r) =>
      (histEmp === "all" || r.user_id === histEmp) &&
      (histStatus === "all" || r.status === histStatus) &&
      (!histFrom || r.end >= histFrom) &&
      (!histTo || r.start <= histTo)
    ),
    [decided, histEmp, histStatus, histFrom, histTo]
  );
  const histFilterOn = histEmp !== "all" || histStatus !== "all" || histFrom || histTo;
  const calRequests = useMemo(
    () => (calEmp === "all" ? requests : requests.filter((r) => r.user_id === calEmp)),
    [requests, calEmp]
  );

  const monthLabel = calMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const monthEntries = useMemo(() => {
    const y = calMonth.getFullYear();
    const m = calMonth.getMonth();
    const first = `${y}-${String(m + 1).padStart(2, "0")}-01`;
    const last = `${y}-${String(m + 1).padStart(2, "0")}-${String(new Date(y, m + 1, 0).getDate()).padStart(2, "0")}`;
    return calRequests
      .filter((r) => r.status !== "denied" && r.start <= last && r.end >= first)
      .sort((a, b) => a.start.localeCompare(b.start) || a.user_name.localeCompare(b.user_name));
  }, [calRequests, calMonth]);

  async function decide(r, status_value, comment) {
    setErr("");
    try {
      if (status_value === "approved") await Requests.approve(r.id, comment || "");
      else await Requests.deny(r.id, comment || "");
      await refreshAll();
    } catch (e) {
      setErr(errMsg(e, "Could not update request."));
    }
  }

  if (showPwd) {
    return (
      <ChangePassword
        user={me}
        forced={false}
        onSaved={async () => { setShowPwd(false); await refreshAll(); }}
        onCancel={() => setShowPwd(false)}
      />
    );
  }

  if (previewEmployee) {
    // Preview-only — pretend to be an employee for UX walkthrough.
    // Build a fake "me" employee shape; requests stay the admin's view.
    const fakeMe = { ...me, role: "employee", balance_hours: 0, must_change_password: false };
    return (
      <div>
        <div style={s.previewBar}>
          <span>👁 Previewing the employee view</span>
          <button data-testid="exit-preview-btn" style={s.previewBtn} onClick={() => setPreviewEmployee(false)}>
            ← Back to Admin
          </button>
        </div>
        <EmployeeView
          me={fakeMe}
          users={users}
          requests={[]}
          refreshAll={refreshAll}
          onLogout={() => setPreviewEmployee(false)}
          previewMode
        />
      </div>
    );
  }

  return (
    <Shell
      header={
        <Header
          title={`Admin — ${me.name}`}
          sub="Approve requests · manage balances · view coverage"
          right={
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button data-testid="admin-preview-btn" style={s.btnGhost} onClick={() => setPreviewEmployee(true)}>View as Employee</button>
              <button data-testid="admin-change-pw-btn" style={s.btnGhost} onClick={() => setShowPwd(true)}>Change Password</button>
              <button data-testid="admin-signout-btn" style={s.btnGhost} onClick={onLogout}>Sign out</button>
            </div>
          }
        />
      }
    >
      <div style={s.tabs}>
        <Tab id="queue" tab={tab} setTab={setTab} testId="admin-tab-queue">
          Pending {pending.length > 0 && <span style={s.count}>{pending.length}</span>}
        </Tab>
        <Tab id="calendar" tab={tab} setTab={setTab} testId="admin-tab-calendar">Coverage</Tab>
        <Tab id="history" tab={tab} setTab={setTab} testId="admin-tab-history">History</Tab>
        <Tab id="team" tab={tab} setTab={setTab} testId="admin-tab-team">Team &amp; Balances</Tab>
        <Tab id="logoff" tab={tab} setTab={setTab} testId="admin-tab-logoff">Log Time Off</Tab>
        <Tab id="report" tab={tab} setTab={setTab} testId="admin-tab-report">Payroll Report</Tab>
        <Tab id="policy" tab={tab} setTab={setTab} testId="admin-tab-policy">PTO Policy</Tab>
      </div>

      {err && <div style={s.error}>{err}</div>}

      {tab === "queue" && (
        <div style={s.card} data-testid="admin-queue">
          <div style={s.cardTitle}>Pending Requests</div>
          {pending.length === 0 && <div style={s.muted}>Nothing waiting on you. 👍</div>}
          {pending.map((r) => {
            const emp = users.find((u) => u.id === r.user_id);
            return (
              <ReqRow key={r.id} r={r} admin
                employeeBalance={emp?.balance_hours}
                employees={users}
                refreshAll={refreshAll}
                onApprove={(req, c) => decide(req, "approved", c)}
                onDeny={(req, c) => decide(req, "denied", c)}
              />
            );
          })}
        </div>
      )}

      {tab === "calendar" && (
        <>
          <div style={s.card}>
            <div style={s.cardTitle}>Team Calendar — Coverage</div>
            <div style={{ ...s.muted, marginBottom: 12 }}>
              Solid bars = approved · dashed = pending. Stacked names on the same day mean overlap to manage.
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }} className="no-print">
              <label style={{ ...s.label, margin: 0 }}>Show:</label>
              <select
                data-testid="coverage-filter-employee"
                style={{ ...s.input, margin: 0, width: "auto", minWidth: 200 }}
                value={calEmp}
                onChange={(e) => setCalEmp(e.target.value)}
              >
                <option value="all">All employees</option>
                {teamMembers.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}{u.active === false ? " (archived)" : ""}</option>
                ))}
              </select>
              <button
                data-testid="coverage-print-btn"
                style={{ ...s.btnPrimary, marginTop: 0, padding: "9px 16px", width: "auto" }}
                onClick={() => window.print()}
              >
                🖨 Print This Month
              </button>
              <span style={{ ...s.muted, fontSize: 12 }}>Tip: choose Landscape in the print dialog for the best fit.</span>
            </div>
            <div id="coverage-print" className="print-area" data-testid="coverage-print-area">
              <div className="print-only" style={s.reportHead}>
                <div style={s.reportBrand}>Tennessee Implant &amp; Oral Surgery</div>
                <div style={s.reportTitle}>Team Time Off — {monthLabel}</div>
                <div style={s.reportGen}>
                  {calEmp !== "all" && `Filtered to: ${teamMembers.find((u) => u.id === calEmp)?.name || ""} · `}
                  Printed {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </div>
              </div>
              <MonthCalendar requests={calRequests} users={users} scope="all" onMonthChange={setCalMonth} />
              <div className="print-only">
                <div style={s.reportSection}>Time Off in {monthLabel}</div>
                {monthEntries.length === 0 && <div style={s.muted}>No time off scheduled this month.</div>}
                {monthEntries.length > 0 && (
                  <table style={s.reportTable}>
                    <thead>
                      <tr>
                        <th style={s.rth}>Employee</th>
                        <th style={s.rth}>Type</th>
                        <th style={s.rth}>Dates</th>
                        <th style={{ ...s.rth, textAlign: "right" }}>Days</th>
                        <th style={{ ...s.rth, textAlign: "right" }}>PTO hrs</th>
                        <th style={s.rth}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthEntries.map((r) => (
                        <tr key={r.id}>
                          <td style={s.rtd}>{r.user_name}</td>
                          <td style={s.rtd}>{r.type_label}</td>
                          <td style={s.rtd}>{fmt(r.start)}{r.end !== r.start ? ` – ${fmt(r.end)}` : ""}</td>
                          <td style={{ ...s.rtd, textAlign: "right" }}>{r.business_days}</td>
                          <td style={{ ...s.rtd, textAlign: "right" }}>{r.hours > 0 ? r.hours : "—"}</td>
                          <td style={{ ...s.rtd, textTransform: "capitalize" }}>{r.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          <div style={s.card}>
            <div style={s.cardTitle}>Upcoming Approved Time Off</div>
            {(() => {
              const upcoming = calRequests
                .filter((r) => r.status === "approved" && new Date(r.end + "T23:59:59") >= new Date())
                .sort((a, b) => new Date(a.start) - new Date(b.start));
              if (upcoming.length === 0) return <div style={s.muted}>No upcoming approved time off.</div>;
              return upcoming.map((r) => <UpcomingRow key={r.id} r={r} refreshAll={refreshAll} />);
            })()}
          </div>
        </>
      )}

      {tab === "history" && (
        <div style={s.card} data-testid="admin-history">
          <div style={s.cardTitle}>Decided Requests</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 14 }}>
            <div>
              <label style={s.label}>Employee</label>
              <select data-testid="history-filter-employee" style={{ ...s.input, margin: 0, width: "auto", minWidth: 180 }} value={histEmp} onChange={(e) => setHistEmp(e.target.value)}>
                <option value="all">All employees</option>
                {teamMembers.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}{u.active === false ? " (archived)" : ""}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={s.label}>Status</label>
              <select data-testid="history-filter-status" style={{ ...s.input, margin: 0, width: "auto" }} value={histStatus} onChange={(e) => setHistStatus(e.target.value)}>
                <option value="all">All</option>
                <option value="approved">Approved</option>
                <option value="denied">Denied</option>
              </select>
            </div>
            <div>
              <label style={s.label}>From</label>
              <input data-testid="history-filter-from" style={{ ...s.input, margin: 0, width: "auto" }} type="date" value={histFrom} onChange={(e) => setHistFrom(e.target.value)} />
            </div>
            <div>
              <label style={s.label}>To</label>
              <input data-testid="history-filter-to" style={{ ...s.input, margin: 0, width: "auto" }} type="date" value={histTo} onChange={(e) => setHistTo(e.target.value)} />
            </div>
            {histFilterOn && (
              <button
                data-testid="history-filter-clear"
                style={{ ...s.btnGhost, padding: "8px 12px", fontSize: 13 }}
                onClick={() => { setHistEmp("all"); setHistStatus("all"); setHistFrom(""); setHistTo(""); }}
              >
                Clear filters
              </button>
            )}
          </div>
          {histFilterOn && (
            <div data-testid="history-filter-count" style={{ ...s.muted, marginBottom: 10 }}>
              Showing {histFiltered.length} of {decided.length} decided request{decided.length === 1 ? "" : "s"}
            </div>
          )}
          {histFiltered.length === 0 && <div style={s.muted}>{histFilterOn ? "No requests match these filters." : "No history yet."}</div>}
          {histFiltered.map((r) => {
            const emp = users.find((u) => u.id === r.user_id);
            return <ReqRow key={r.id} r={r} admin employeeBalance={emp?.balance_hours} employees={users} refreshAll={refreshAll} />;
          })}
        </div>
      )}

      {tab === "team" && <TeamAdmin users={users} requests={requests} refreshAll={refreshAll} />}
      {tab === "logoff" && <LogTimeOff users={users} requests={requests} refreshAll={refreshAll} />}
      {tab === "report" && <PtoReport requests={requests} users={users} />}
      {tab === "policy" && <PolicyTab />}
    </Shell>
  );
}
